import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  CheckCircle2, AlertTriangle, XCircle, RefreshCw, ArrowDownAZ,
  Calendar, Image as ImageIcon, Search,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  complete: { label: "100% complet", color: "bg-emerald-500 text-white", Icon: CheckCircle2 },
  good: { label: "≥ 80%", color: "bg-emerald-400 text-white", Icon: CheckCircle2 },
  partial: { label: "Partiel", color: "bg-amber-500 text-white", Icon: AlertTriangle },
  missing: { label: "Manquant", color: "bg-rose-500 text-white", Icon: XCircle },
};

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "missing", label: "Manquants" },
  { key: "partial", label: "Partiels" },
  { key: "good", label: "Bons" },
  { key: "complete", label: "Complets" },
];

const SORTS = [
  { key: "release", label: "Plus récent" },
  { key: "release_asc", label: "Plus ancien" },
  { key: "eu_pct", label: "Moins de prix EU" },
  { key: "us_pct", label: "Moins de prix US" },
  { key: "name", label: "Nom (A→Z)" },
];

function sortItems(items, key) {
  const arr = [...items];
  switch (key) {
    case "release_asc": return arr.sort((a, b) => (a.release_date || "").localeCompare(b.release_date || ""));
    case "eu_pct": return arr.sort((a, b) => a.eu_pct - b.eu_pct);
    case "us_pct": return arr.sort((a, b) => a.us_pct - b.us_pct);
    case "name": return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    case "release":
    default: return arr.sort((a, b) => (b.release_date || "").localeCompare(a.release_date || ""));
  }
}

function ProgressBar({ pct }) {
  const color = pct >= 95 ? "bg-emerald-500" : pct >= 70 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full transition-all ${color}`} style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  );
}

export default function AdminPriceAudit() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("missing");
  const [sortKey, setSortKey] = useState("release");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/price-audit");
      setData(data);
    } catch {
      toast.error("Erreur de chargement de l'audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const items = data?.items || [];
  const counts = data?.counts || {};

  const filtered = items.filter((it) => {
    if (filter !== "all" && it.status !== filter) return false;
    if (query && !`${it.id} ${it.name}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const sorted = sortItems(filtered, sortKey);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-price-audit-page">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-10 pb-20">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <span className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold">// ADMIN · PRICE AUDIT</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">
              Audit des prix par série
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Couverture EU (Cardmarket) et US (TCGplayer) sur chaque série indexée.
            </p>
          </div>
          <button
            onClick={load}
            data-testid="audit-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-white text-slate-700 hover:border-slate-400 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { key: "complete", label: "Complets" },
            { key: "good", label: "Bons (≥80%)" },
            { key: "partial", label: "Partiels" },
            { key: "missing", label: "Manquants" },
          ].map(({ key, label }) => {
            const meta = STATUS_META[key];
            const Icon = meta.Icon;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`audit-kpi-${key}`}
                className={`text-left p-4 rounded-2xl border bg-white transition ${
                  filter === key ? "border-slate-900 shadow" : "border-slate-200 hover:border-slate-400"
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-2 ${meta.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="text-2xl font-extrabold font-heading text-slate-900">{counts[key] || 0}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
              </button>
            );
          })}
        </div>

        {/* Filters bar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`audit-filter-${f.key}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-xl transition ${
                  filter === f.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher série / ID…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="audit-search"
              className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-slate-200 focus:border-slate-900 focus:outline-none"
            />
          </div>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            data-testid="audit-sort"
            className="text-xs px-3 py-1.5 rounded-xl border border-slate-200 bg-white focus:border-slate-900 focus:outline-none"
          >
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {/* Items */}
        {loading && !data ? (
          <div className="p-12 text-center text-sm text-slate-400">Chargement…</div>
        ) : sorted.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400" data-testid="audit-empty">
            Aucune série ne correspond à ces filtres.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
            {sorted.map((it) => {
              const meta = STATUS_META[it.status];
              const Icon = meta.Icon;
              return (
                <div
                  key={it.id}
                  data-testid={`audit-row-${it.id}`}
                  className="p-4 lg:p-5 grid grid-cols-[auto_1fr_auto] lg:grid-cols-[auto_1fr_1.5fr_auto] items-center gap-4 hover:bg-slate-50 transition"
                >
                  <div className="w-14 h-10 flex items-center justify-center bg-slate-50 rounded-lg overflow-hidden">
                    {it.logo ? (
                      <img src={it.logo} alt="" className="max-h-9 max-w-12 object-contain" loading="lazy" />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-slate-300" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm text-slate-900 truncate">{it.name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                      <span className="font-mono">{it.id}</span>
                      {it.release_date && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{it.release_date}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>{it.total_cards} cartes</span>
                    </div>
                  </div>
                  <div className="hidden lg:block">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                          <span>🇪🇺 EU</span><span className="font-bold">{it.eu_pct}%</span>
                        </div>
                        <ProgressBar pct={it.eu_pct} />
                        <div className="text-[10px] text-slate-400 mt-0.5">{it.eu_cards}/{it.total_cards}</div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                          <span>🇺🇸 US</span><span className="font-bold">{it.us_pct}%</span>
                        </div>
                        <ProgressBar pct={it.us_pct} />
                        <div className="text-[10px] text-slate-400 mt-0.5">{it.us_cards}/{it.total_cards}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md ${meta.color}`}>
                      <Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {data?.checked_at && (
          <p className="text-[10px] text-slate-400 mt-4 font-mono uppercase tracking-wider">
            Dernière vérification : {new Date(data.checked_at).toLocaleString("fr-FR")}
          </p>
        )}
      </div>
    </div>
  );
}
